(function(angular, _) {
    /**
     * @ngdoc object
     * @name ComponentStateController
     * @description Directive that reflects the state of the component in the form of DOM attributes (eg. CSS classes)
     * allowing the external parties (eg. Selenium tests) to identify the state of the component by the DOM attributes
     *
     * A component lifecycle is considered to go through the following states:
     * 1. Initialising: The component is constructed, but is performing some async operations to fetch the critical resources
     * 2. Ready: The $onInit phase and all async operations triggered inside it are complete, which means the View has meaningful data
     * 3. Loading: Any subsequent state of loading caused by async operations after the component was in ready state (mostly triggered by user interaction)
     *
     * Initialising > Ready > Loading > Ready > Loading > Ready
     */
    class ComponentStateController {
        static get $inject() {
            return ['$timeout', '$element'];
        }

        constructor($timeout, $element) {
            this.$timeout = $timeout;
            this.$element = $element;

            /**
             * The CSS class added to the element on first loading (currently not in use)
             * @type {string}
             */
            this.classInitialising = 'component-state-initialising';

            /**
             * The CSS class added to the element on loading
             * @type {string}
             */
            this.classLoading = 'component-state-loading';

            /**
             * The CSS class added to the host element on loading complete (async tasks are done)
             * @type {string}
             */
            this.classReady = 'component-state-ready';

            /**
             * Indicates that the first loading state has complete, helping distinguish the component initialisation state from subsequent loading states.
             * @type {boolean}
             */
            this.initComplete = false;

            this.timeouts = {};
        }

        $onInit() {
            return this.onStateChange();
        }

        $onChanges() {
            return this.onStateChange();
        }

        $onDestroy() {
            _.values(this.timeouts, (timeout) => this.$timeout.cancel(timeout));
        }

        /**
         * @ngdoc method
         * @name ComponentStateController#onStateChange
         * @description Triggers the DOM update to reflect the current state of the component
         * @returns {ComponentStateController}
         */
        onStateChange() {
            if (!this.initComplete && !this.isLoading) {
                this.initComplete = true;
            }

            if (this.isLoading) {
                return this.onLoadingState();
            }

            return this.onReadyState();
        }

        /**
         * @ngdoc method
         * @name ComponentStateController#resetState
         * @description Resets the state by removing the state attributes from the DOM
         * @returns {ComponentStateController}
         */
        resetState() {
            this.$element.removeClass(this.classInitialising);
            this.$element.removeClass(this.classLoading);
            this.$element.removeClass(this.classReady);

            return this;
        }

        /**
         * @ngdoc method
         * @name ComponentStateController#onLoadingState
         * @description Reflects the initialisation and loading state in the DOM
         * @returns {ComponentStateController}
         */
        onLoadingState() {
            if (!this.timeouts.onLoading) {
                this.timeouts.onLoading = this.$timeout(() => {
                    this.resetState();
                    this.$element.addClass(
                        this.classLoading
                        // this.initComplete ? this.classLoading : this.classInitialising // TODO Check if selenium is impacted if added a third state
                    );
                    delete this.timeouts.onLoading;
                });
            }

            return this;
        }

        /**
         * @ngdoc method
         * @name ComponentStateController#onReadyState
         * @description Reflects the ready and loading state in the DOM
         * @returns {ComponentStateController}
         */
        onReadyState() {
            if (!this.timeouts.onReady) {
                this.timeouts.onReady = this.$timeout(() => {
                    this.resetState();
                    this.$element.addClass(this.classReady);
                    delete this.timeouts.onReady;
                });
            }

            return this;
        }
    }

    angular.module('wtUi')
        .directive('componentState', [function() {
            return {
                scope: {
                    /**
                     * Provides the current loading state of the host component
                     * @type {boolean}
                     * @example <host-component component-state="$ctrl.loading"></host-component>
                     */
                    isLoading: '<?componentState'
                },
                bindToController: true,
                restrict: 'A',
                controller: ComponentStateController
            };
        }]);
})(window.angular, window._);
